import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { IconButton } from '@cbrebuild/blocks';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import VOUCHER_ROUTES from '../../../page-templates/voucher/voucher-subroutes';
import { formatItemsInLines, getAddressString, validateAddress } from '../voucher-validation';
import RequiredLabeledItem from './RequiredLabeledItem';
import { useRequiredFieldsRegistry } from './RequiredFieldsContext';
import { fetchDealContacts } from '../../../redux/actions/deal-contacts-actions';
import { getRoleKey, isPartyPocRequired } from '../../../utils/transaction-utils';

const BillToReview = ({ brokerVerificationEnabled }) => {
  const {
    contacts, deal, voucher,
  } = useSelector(state => ({
    contacts: state.dealContacts.contacts,
    deal: state.deal.deal,
    voucher: state.voucher.voucher,
  }));

  const history = useHistory();
  const dispatch = useDispatch();
  const isOtherBilledParty = deal.billed_to_type === 'Other';
  // Only needed for the broker-verification panel, so nothing extra is fetched when the flag is off.
  const watchOtherParty = brokerVerificationEnabled && isOtherBilledParty;
  // The "Other" contact is only judged missing once contacts have actually been fetched, so the
  // momentary empty Redux list isn't mistaken for a contact the user never filled in.
  const [contactsLoaded, setContactsLoaded] = useState(false);
  useEffect(() => {
    if (!watchOtherParty) return undefined;
    let cancelled = false;
    // Fail open (also on a rejected fetch) so a request error can't hide the panel row forever.
    const markLoaded = () => { if (!cancelled) setContactsLoaded(true); };
    Promise.resolve(dispatch(fetchDealContacts({
      deal: deal.id,
      ordering: '-is_primary',
      page: 1,
      page_size: 100,
    }))).then(markLoaded, markLoaded);
    return () => { cancelled = true; };
  }, [watchOtherParty, deal.id, dispatch]);
  const url = VOUCHER_ROUTES[2].routes[4].path.replace(':id', voucher.deal);
  let billToCompany;
  let contactType;

  // A null billed_to_type means the user never visited the Bill To tab —
  // mark the voucher invalid so the review banner fires and Continue is disabled.
  if (!deal.billed_to_type) {
    localStorage.setItem('voucher_valid', JSON.stringify(false));
  }

  // "Billed To" isn't rendered via a generic control -- it's the ENTIRE real Bill To tab
  // UI (the "Who is the Billable Party?" RadioGroup/RadioButton picker + company/contact
  // preview), reused verbatim via its connected container (BillToTabContainer, rendered by
  // RequiredFieldsSummary's customRowType renderer) so the panel's design and behavior are
  // identical to the live Financials tab, not a re-implementation of it.
  const registry = useRequiredFieldsRegistry();
  const registerRequiredField = registry?.registerRequiredField;
  const unregisterRequiredField = registry?.unregisterRequiredField;
  // Latches to `true` the first time "Billed To" is found missing and never resets -- gates
  // whether this row EVER shows up in the "Before you submit" panel at all, so a deal the
  // user already set a billing party on earlier in the voucher flow doesn't show up here
  // just because they revisited the Review screen (2026-07-31 product feedback).
  const everMissingRef = useRef(false);

  // Unregister only on true unmount -- see the matching effect in RequiredLabeledItem.js.
  // Harmless no-op if this row was never registered above (never missing).
  const unmountRef = useRef();
  unmountRef.current = () => {
    if (unregisterRequiredField) unregisterRequiredField('bill-to-billed-to-type');
  };
  useEffect(() => () => unmountRef.current(), []);


  const billToPartyType = deal.billed_to_type?.toLowerCase();
  if (deal.billed_to_type === 'Client') {
    billToCompany = deal.client;
    contactType = 'consulting';
  } else {
    billToCompany = deal[getRoleKey(deal.rep_role, billToPartyType)];
    contactType = billToPartyType;
  }
  const isBillToPocRequired = isPartyPocRequired(deal, billToPartyType);
  const billToCompanyLine = formatItemsInLines([
    billToCompany?.name || '',
    getAddressString(billToCompany),
  ]);
  const contact = contacts?.filter(({ contact_type }) => contact_type === contactType)[0];
  let nameAndTitle = '';
  const otherContact = contact;
  if (contact) {
    if (!contact.email_addresses) {
      contact.email_addresses = [];
    }
    if (!contact.phone_numbers) {
      contact.phone_numbers = [];
    }
    if (otherContact && otherContact.given_name && otherContact.surname) {
      otherContact.fullName = otherContact.given_name.concat(' ', otherContact.surname);
    }
    if (!contact.email_addresses[0]) {
      contact.email_addresses.push({ type: 'work', address: '', default: false });
    }
    if (contact?.given_name && contact.surname) {
      nameAndTitle = `${contact.given_name} ${contact.surname}`;
    } else {
      nameAndTitle = contact.given_name || contact.surname;
    }

    if (nameAndTitle && nameAndTitle.length && contact.job_title) {
      nameAndTitle += ` - ${contact.job_title}`;
    } else if (contact.job_title) {
      nameAndTitle += contact.job_title;
    }
  }

  const hasAllContactInfo = contact && contact.given_name && contact.surname
    && contact.email_addresses?.[0]?.address && contact.phone_numbers?.[0]?.number;
  if (isBillToPocRequired && contact !== undefined && !hasAllContactInfo) {
    localStorage.setItem('voucher_valid', JSON.stringify(false));
  }
  const billToContactLine = formatItemsInLines([
    nameAndTitle,
    contact?.email_addresses && contact.email_addresses[0] && contact.email_addresses[0].address,
    contact?.phone_numbers && contact.phone_numbers[0] && contact.phone_numbers[0].number,
  ]);
  const billedPartyAddressString = validateAddress(deal.billed_party) ? deal.billed_party : '';

  // "Other" shows the whole editable Bill To form in the panel whenever its company or contact
  // is incomplete (Company Name/Address, and the contact's Name/Phone/Email), not just when
  // Billed To itself is unset.
  const otherPartyIncomplete = watchOtherParty && contactsLoaded && (
    !deal.billed_party?.name
    || !billedPartyAddressString
    || !otherContact?.fullName
    || !otherContact?.phone_numbers?.[0]?.number
    || !otherContact?.email_addresses?.[0]?.address
  );
  const billToNeedsAttention = !deal.billed_to_type || otherPartyIncomplete;
  useEffect(() => {
    if (!registerRequiredField) return;
    if (billToNeedsAttention) everMissingRef.current = true;
    if (!everMissingRef.current) return;
    // Stays registered once it has ever needed attention, with `complete` reflecting whether it
    // still does -- see the matching pattern in RequiredLabeledItem.js.
    registerRequiredField('bill-to-billed-to-type', {
      section: 'Bill To',
      label: 'Billed To',
      requiredMessage: deal.billed_to_type ? 'Bill To company and contact information is required' : 'Billed To is required',
      customRowType: 'billTo',
      rowId: 'bill-to-party',
      complete: !billToNeedsAttention,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billToNeedsAttention, deal.billed_to_type, registerRequiredField]);

  return (
    <div className="voucher-review-group bill-to-review">
      <div className="navigation">
        <Link className="h2 link" to={url}>Bill To</Link>
        <IconButton iconName="edit" onClick={() => { history.push(url); }} />
      </div>
      <div className="content">
        {!deal.billed_to_type
          ? <h3 className="voucher-error-text">Billed To is required</h3>
          : (
            <>
              {deal.billed_to_type !== 'Other' && (
                <h3>Billed to {deal.billed_to_type}</h3>
              )}
              {deal.billed_to_type !== 'Other' && (billToCompanyLine ?
                <div>
                  <div className="semibold">Company Info:</div>
                  <pre className="p">{billToCompanyLine}</pre>
                </div>
                : '')
              }
              {deal.billed_to_type !== 'Other' && (contact ?
                <div>
                  <div className="semibold">Contact Info:</div>
                  <pre className="p">{billToContactLine}</pre>
                  {isBillToPocRequired && !hasAllContactInfo && <p className="error-message">Missing required contact information</p>}
                </div>
                : '')
              }
            </>
          )
        }
        {deal.billed_to_type === 'Other' ?
          <div className="two-cols">
            <div className="col-left">
              <h3>Company</h3>
              <RequiredLabeledItem
                label="Company Name"
                item={deal.billed_party?.name}
                isReview
              />
              <RequiredLabeledItem label="Pre-Agreement ID" item={deal.billed_party?.pre_agreement_id} notRequired isReview />
              <RequiredLabeledItem label="Address" format="address" item={billedPartyAddressString} isReview />
            </div>
            <div className="col-right">
              <h3>Contact</h3>
              <RequiredLabeledItem label="Name" item={otherContact?.fullName} isReview />
              <RequiredLabeledItem label="Phone #" item={otherContact?.phone_numbers[0]?.number} isReview />
              <RequiredLabeledItem label="Email Address" item={otherContact?.email_addresses[0]?.address} isReview />
              <RequiredLabeledItem label="Job Title" item={otherContact?.job_title} notRequired isReview />
            </div>
          </div>
          : ''
        }
        {deal.billed_to_type === 'Outside Broker' &&
          <div className="two-cols">
            <div className="col-left">
              <h3>Company</h3>
              <RequiredLabeledItem
                label="Company Name"
                item={deal.billed_party?.name}
                isReview
                fieldId="bill-to-company-name"
                section="Bill To"
                linkTo={url}
              />
              <RequiredLabeledItem label="Address" format="address" item={billedPartyAddressString} isReview />
            </div>
          </div>
        }
      </div>
    </div>
  );
};

BillToReview.propTypes = {
  brokerVerificationEnabled: PropTypes.bool,
};

BillToReview.defaultProps = {
  brokerVerificationEnabled: false,
};

export default BillToReview;
